var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import Icon from '@mui/material/SvgIcon';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckedBoxIcon from '@mui/icons-material/CheckBoxOutlined';
import cx from 'classnames';
import { Tooltip } from '../';
import useKeyboardEscapeHandler from '../hooks/useKeybordEscapeHandler';

var Item = function Item(_ref) {
  var _extends2, _cx, _cx2, _cx3, _cx4, _cx5;

  var classes = _ref.classes,
      id = _ref.id,
      label = _ref.label,
      _ref$description = _ref.description,
      description = _ref$description === undefined ? '' : _ref$description,
      isSelected = _ref.isSelected,
      isDisabled = _ref.isDisabled,
      isGreyed = _ref.isGreyed,
      changeList = _ref.changeList,
      ariaLabel = _ref.ariaLabel,
      NavigateIcon = _ref.NavigateIcon,
      hasChild = _ref.hasChild,
      hasChildrenOnLevel = _ref.hasChildrenOnLevel,
      count = _ref.count,
      svgPath = _ref.svgPath,
      style = _ref.style,
      isRtl = _ref.isRtl,
      accessibility = _ref.accessibility,
      HTMLRenderer = _ref.HTMLRenderer,
      hierarchicalId = _ref.hierarchicalId,
      labels = _ref.labels,
      _ref$depth = _ref.depth,
      depth = _ref$depth === undefined ? 0 : _ref$depth,
      eventsListeners = _ref.eventsListeners,
      expanded = _ref.expanded,
      highlightedLabel = _ref.highlightedLabel;

  var _useKeyboardEscapeHan = useKeyboardEscapeHandler(),
      open = _useKeyboardEscapeHan.open,
      setOpen = _useKeyboardEscapeHan.setOpen;

  var padding = depth * 30 + (depth > 0 && !hasChildrenOnLevel ? 30 : 0) + (!hasChild && hasChildrenOnLevel ? 30 : 0);

  var formattedLabel = highlightedLabel ? React.createElement('div', { dangerouslySetInnerHTML: { __html: highlightedLabel } }) : label;
  return React.createElement(
    ListItem,
    {
      dense: true,
      disableGutters: true,
      classes: { disabled: classes.disabled },
      className: cx(classes.listItemContainer, classes.disableSecondaryAction),
      tabIndex: -1,
      style: _extends({}, style, (_extends2 = {
        flexDirection: isRtl ? 'row-reverse' : 'row'
      }, _extends2['' + (isRtl ? 'paddingRight' : 'paddingLeft')] = padding + 'px', _extends2)),
      role: 'listitem'
    },
    R.and(NavigateIcon, hasChild) && React.createElement(
      IconButton,
      {
        disableRipple: true,
        disableFocusRipple: true,
        className: classes.arrow,
        'aria-label': ariaLabel,
        tabIndex: 0,
        size: 'small',
        color: 'primary',
        onClick: function onClick() {
          return changeList(hierarchicalId || id);
        },
        'aria-expanded': expanded
      },
      React.createElement(NavigateIcon, null)
    ),
    React.createElement(ListItemText, {
      'data-testid': 'value_' + id,
      tabIndex: 0,
      role: 'checkbox',
      'aria-label': isGreyed ? R.propOr('', 'disableItemLabel')(labels) + ' ' + label : label,
      'aria-checked': isSelected,
      'aria-disabled': isDisabled,
      primaryTypographyProps: { noWrap: true, color: 'inherit' },
      secondaryTypographyProps: { color: 'inherit' },
      classes: { root: classes.listItem },
      className: cx((_cx = {}, _cx[classes.accessibilityItemHover] = accessibility, _cx[classes.listItemSelected] = isSelected, _cx[classes.listItemHover] = !isDisabled, _cx)),
      onKeyDown: isDisabled ? null : eventsListeners.onKeyDown,
      onClick: isDisabled ? null : eventsListeners.onClick,
      onMouseDown: isDisabled || open ? null : eventsListeners.onMouseDown,
      onMouseEnter: isDisabled || open ? null : eventsListeners.onMouseEnter,
      title: isGreyed ? R.propOr('', 'disableItemLabel')(labels) + ' ' + label : label,
      style: {
        opacity: isDisabled && isGreyed ? '0.5' : '1',
        outline: accessibility ? undefined : 'none'
      },
      primary: React.createElement(
        'div',
        {
          className: cx(classes.labelContainer, (_cx2 = {}, _cx2[classes.rtlLabelContainer] = isRtl, _cx2))
        },
        React.createElement(
          Icon,
          {
            color: 'primary',
            className: cx(classes.labelIcon, (_cx3 = {}, _cx3[classes.disabledLabel] = isDisabled, _cx3)),
            style: { opacity: isDisabled ? '0.5' : '1' }
          },
          isSelected ? React.createElement(CheckedBoxIcon, null) : React.createElement(CheckBoxIcon, null)
        ),
        svgPath && React.createElement(
          SvgIcon,
          {
            color: 'primary',
            className: cx(classes.labelIcon, (_cx4 = {}, _cx4[classes.disabledLabel] = isDisabled, _cx4))
          },
          React.createElement('path', { d: svgPath })
        ),
        React.createElement(
          Tooltip,
          {
            title: R.or(R.isNil(HTMLRenderer), R.isEmpty(description)) ? description : React.createElement(HTMLRenderer, { html: description, onClick: function onClick(e) {
                return e.stopPropagation();
              } }),
            enterDelay: 400,
            enterNextDelay: 400,
            tabIndex: description ? 0 : -1,
            'aria-label': description,
            'aria-hidden': false,
            placement: 'top',
            disableInteractive: Boolean(HTMLRenderer) === false,
            onOpen: function onOpen() {
              return setOpen(true);
            },
            onClose: function onClose() {
              return setOpen(false);
            },
            disableHoverListener: R.isEmpty(description),
            open: open
          },
          React.createElement(
            Typography,
            {
              color: 'inherit',
              variant: 'body2',
              className: cx(classes.label, (_cx5 = {}, _cx5[classes.disabledLabel] = isGreyed, _cx5[classes.tooltipNotice] = !R.isEmpty(description), _cx5))
            },
            formattedLabel
          )
        ),
        R.not(R.isNil(count)) && React.createElement(
          Typography,
          { variant: 'body2', className: classes.count },
          '(',
          count,
          ')'
        )
      )
    })
  );
};

Item.propTypes = process.env.NODE_ENV !== "production" ? {
  classes: PropTypes.object,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  description: PropTypes.string,
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ariaLabel: PropTypes.string,
  NavigateIcon: PropTypes.object,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hasChild: PropTypes.bool,
  hasChildrenOnLevel: PropTypes.bool,
  isSection: PropTypes.bool,
  changeList: PropTypes.func,
  changeListDescription: PropTypes.string,
  count: PropTypes.number,
  svgPath: PropTypes.string,
  style: PropTypes.object,
  isRtl: PropTypes.bool,
  accessibility: PropTypes.bool,
  labelRenderer: PropTypes.func,
  HTMLRenderer: PropTypes.func,
  hierarchicalId: PropTypes.string,
  labels: PropTypes.object,
  depth: PropTypes.number,
  eventsListeners: PropTypes.object,
  isGreyed: PropTypes.bool,
  expanded: PropTypes.bool,
  highlightedLabel: PropTypes.string
} : {};

export default Item;